import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-4aee0d88"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "map" }
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", {
  id: "map",
  class: "map__window"
}, null, -1))
const _hoisted_3 = [
  _hoisted_2
]

import { DEFAULT_COORDINATES } from '@/enums/services'
import {
  computed,
  createApp,
  onMounted,
  onUnmounted,
  PropType,
  ref,
  shallowRef,
  watch,
  watchEffect,
} from 'vue'
import { PublicService } from '@/models/public/bookingWidget.model'
import InfoWindowContent from '@/components/public/route-planner/InfoWindowContent.vue'
import OrderItemInfoWindowContent from '@/components/public/route-planner/OrderItemInfoWindowContent.vue'
import { Route, RouteStop } from '@/models/public/routePlanner.model'
import isBefore from 'date-fns/isBefore'


export default /*#__PURE__*/_defineComponent({
  __name: 'RouteMap',
  props: {
  route: {
    type: Object as PropType<Route | null>,
    default: null,
  },
  services: {
    type: Array as PropType<PublicService[]>,
    required: true,
  },
  selectedService: {
    type: Object as PropType<PublicService | null>,
    default: null,
  },
  disableRoutePlanning: {
    type: Boolean,
    default: false,
  },
  showOnlyOrderItems: {
    type: Boolean,
    default: false,
  },
},
  setup(__props, { emit }: { emit: ({
  onAdd: [value: any]
  onUpdateSegments: [route: any]
}), expose: any, slots: any, attrs: any }) {

const props = __props





const API_KEY = process.env.VUE_APP_GOOGLE_API_KEY
const GOOGLE_MAPS_URL = `https://maps.googleapis.com/maps/api/js?key=${API_KEY}&callback=initMap`

const googleMapsRef = shallowRef<{
  map: any
  directionsService: any
  directionsRenderer: any
  infoWindow: any
  markerLib: any
}>()

const markersMap = ref(new Map<string, any>())
const routeMarkers = ref<{ start?: any; end?: any }>({})
const resizeObserver = ref<ResizeObserver>()
const mapElement = ref<HTMLElement>()

const serviceLocations = computed(() => {
  const servicesData =
    props.showOnlyOrderItems && props.route?.stops
      ? props.route.stops.map((s) => s.service)
      : props.services

  const locationMap = new Map<
    string,
    {
      coords: { lat: number; lng: number }
      services: PublicService[]
      routeStops: RouteStop[]
    }
  >()

  servicesData.forEach((service) => {
    if (!service.location) return

    const lat = +service.location.latitude_decimal_degree
    const lng = +service.location.longitude_decimal_degree
    const key = `${lat},${lng}`

    if (!locationMap.has(key)) {
      locationMap.set(key, {
        coords: { lat, lng },
        services: [],
        routeStops: [],
      })
    }

    const location = locationMap.get(key)!
    location.services.push(service)

    if (props.showOnlyOrderItems && props.route?.stops) {
      const stop = props.route.stops.find((s) => s.service.id === service.id)
      if (stop) location.routeStops.push(stop)
    }
  })

  return Array.from(locationMap.values())
})

const routeData = computed(() => {
  if (!props.route?.start_point || !props.route?.end_point) return null

  return {
    origin: {
      lat: +props.route.start_point.latitude_decimal_degree,
      lng: +props.route.start_point.longitude_decimal_degree,
    },
    destination: {
      lat: +props.route.end_point.latitude_decimal_degree,
      lng: +props.route.end_point.longitude_decimal_degree,
    },
    waypoints:
      props.route.stops?.map((stop) => ({
        lat: +stop.service.location.latitude_decimal_degree,
        lng: +stop.service.location.longitude_decimal_degree,
      })) || [],
    departureTime: getDepartureTime(),
  }
})

const markerStates = computed(() => {
  const states = new Map<
    string,
    { selected: boolean; inRoute: boolean; orderIndex: number }
  >()

  serviceLocations.value.forEach((location) => {
    const key = `${location.coords.lat},${location.coords.lng}`
    const selected = location.services.some(
      (s) => s.id === props.selectedService?.id,
    )
    const inRoute = location.services.some((s) =>
      props.route?.stops.some((stop) => stop.service.id === s.id),
    )

    let orderIndex = -1
    if (inRoute && props.route?.stops) {
      const firstServiceInRoute = location.services.find((s) =>
        props.route!.stops.some((stop) => stop.service.id === s.id),
      )
      if (firstServiceInRoute) {
        orderIndex = props.route.stops.findIndex(
          (stop) => stop.service.id === firstServiceInRoute.id,
        )
      }
    }

    states.set(key, { selected, inRoute, orderIndex })
  })

  return states
})

onMounted(() => {
  mapElement.value = document.getElementById('map') as HTMLElement
  initializeGoogleMaps()
})

onUnmounted(() => {
  cleanup()
})

watch(
  serviceLocations,
  () => {
    if (googleMapsRef.value) updateMarkers()
  },
  { deep: true },
)

watch(
  markerStates,
  () => {
    if (googleMapsRef.value) updateMarkerStyles()
  },
  { deep: true },
)

watchEffect(() => {
  if (!googleMapsRef.value || !routeData.value) return
  renderRoute()
})

function getDepartureTime(): Date {
  if (!props.route?.start_time) return new Date()
  const time =
    typeof props.route.start_time === 'string'
      ? new Date(props.route.start_time)
      : props.route.start_time
  return isBefore(time, new Date()) ? new Date() : time
}

function cleanup() {
  resizeObserver.value?.disconnect()
  markersMap.value.forEach((marker) => marker.setMap(null))
  markersMap.value.clear()

  if (routeMarkers.value.start) {
    routeMarkers.value.start.setMap(null)
    routeMarkers.value.start = undefined
  }
  if (routeMarkers.value.end) {
    routeMarkers.value.end.setMap(null)
    routeMarkers.value.end = undefined
  }

  const scripts = Array.from(document.getElementsByTagName('script'))
  scripts.forEach((script) => {
    if (script.src === GOOGLE_MAPS_URL) {
      script.remove()
    }
  })

  delete window.initMap
}

function initializeGoogleMaps() {
  window.initMap = async () => {
    const { Map, InfoWindow, DirectionsService, DirectionsRenderer } =
      window.google.maps
    const { AdvancedMarkerElement, PinElement } =
      await window.google.maps.importLibrary('marker')

    const map = new Map(mapElement.value, {
      center: {
        lat: DEFAULT_COORDINATES.LATITUDE_ESTONIA,
        lng: DEFAULT_COORDINATES.LONGITUDE_ESTONIA,
      },
      zoom: 8,
      mapId: 'ROUTE_PLANNER',
      gestureHandling: 'greedy',
      disableDefaultUI: false,
      zoomControl: true,
      mapTypeControl: false,
      scaleControl: false,
      streetViewControl: false,
      rotateControl: false,
      fullscreenControl: false,
    })

    const directionsRenderer = new DirectionsRenderer({
      suppressMarkers: true,
      suppressInfoWindows: true,
    })
    directionsRenderer.setMap(map)

    googleMapsRef.value = {
      map,
      directionsService: new DirectionsService(),
      directionsRenderer,
      infoWindow: new InfoWindow(),
      markerLib: { AdvancedMarkerElement, PinElement },
    }

    setupEventHandlers()
    updateMarkers()
    if (routeData.value) renderRoute()
  }

  if (!document.querySelector(`script[src="${GOOGLE_MAPS_URL}"]`)) {
    const script = document.createElement('script')
    script.src = GOOGLE_MAPS_URL
    script.async = true
    document.head.appendChild(script)
  }
}

function setupEventHandlers() {
  const { map } = googleMapsRef.value!

  if (typeof ResizeObserver !== 'undefined' && mapElement.value) {
    resizeObserver.value = new ResizeObserver(() => {
      window.google.maps.event.trigger(map, 'resize')
    })
    resizeObserver.value.observe(mapElement.value)
  }

  setupTouchControls()
}

function setupTouchControls() {
  if (!mapElement.value) return

  let touchActive = false

  const handleTouch = (e: TouchEvent, active: boolean) => {
    touchActive = active
    if (active) e.stopPropagation()
  }

  mapElement.value.addEventListener('touchstart', (e) => handleTouch(e, true), {
    passive: true,
  })
  mapElement.value.addEventListener(
    'touchmove',
    (e) => {
      if (touchActive) e.stopPropagation()
    },
    { passive: false },
  )
  mapElement.value.addEventListener(
    'touchend',
    (e) => {
      handleTouch(e, false)
      setTimeout(() => {
        touchActive = false
      }, 100)
    },
    { passive: true },
  )
}

function updateMarkers() {
  if (!googleMapsRef.value) return

  const {
    map,
    markerLib: { AdvancedMarkerElement },
  } = googleMapsRef.value
  const activeKeys = new Set<string>()

  serviceLocations.value.forEach((location) => {
    const key = `${location.coords.lat},${location.coords.lng}`
    activeKeys.add(key)

    const marker = new AdvancedMarkerElement({
      map,
      position: location.coords,
      gmpClickable: true,
      zIndex: 1000,
    })

    marker.addListener('gmp-click', () => {
      showInfoWindow(marker, location)
    })

    markersMap.value.set(key, marker)
  })

  markersMap.value.forEach((marker, key) => {
    if (!activeKeys.has(key)) {
      marker.setMap(null)
      markersMap.value.delete(key)
    }
  })

  updateMarkerStyles()
}

function updateMarkerStyles() {
  if (!googleMapsRef.value) return

  const {
    markerLib: { PinElement },
  } = googleMapsRef.value

  markersMap.value.forEach((marker, key) => {
    const state = markerStates.value.get(key)
    if (!state) return

    const colors = state.selected
      ? { bg: '#ffd700', border: '#ffac00', glyph: '#ffac00' }
      : state.inRoute
      ? { bg: '#6cecff', border: '#00acff', glyph: '#00acff' }
      : { bg: '#009e21', border: '#74ff82', glyph: '#74ff82' }

    const glyph =
      state.inRoute && state.orderIndex >= 0 && routeData.value
        ? String.fromCharCode(66 + state.orderIndex)
        : null

    const pin = new PinElement({
      background: colors.bg,
      borderColor: colors.border,
      glyphColor: colors.glyph,
      glyph,
    })

    marker.content = pin.element
  })
}

function showInfoWindow(
  marker: any,
  location: typeof serviceLocations.value[0],
) {
  const { infoWindow } = googleMapsRef.value!
  const content = document.createElement('div')

  const Component =
    props.showOnlyOrderItems && location.routeStops.length
      ? OrderItemInfoWindowContent
      : InfoWindowContent

  const componentProps =
    props.showOnlyOrderItems && location.routeStops.length
      ? { routeStops: location.routeStops }
      : {
          services: location.services,
          onAdd: (value: any) => emit('onAdd', value),
          disableRoutePlanning: props.disableRoutePlanning,
        }

  createApp(Component, componentProps).mount(content)

  infoWindow.setContent(content)
  infoWindow.open({ anchor: marker, map: googleMapsRef.value!.map })
}

function createRouteMarkers() {
  if (!googleMapsRef.value || !routeData.value) return

  const {
    map,
    markerLib: { AdvancedMarkerElement, PinElement },
  } = googleMapsRef.value

  if (routeMarkers.value.start) {
    routeMarkers.value.start.setMap(null)
  }
  if (routeMarkers.value.end) {
    routeMarkers.value.end.setMap(null)
  }

  const startPin = new PinElement({
    background: '#FF4B4B',
    borderColor: '#C41E3A',
    glyphColor: '#FFFFFF',
    glyph: 'A',
    scale: 0.8,
  })

  const endPin = new PinElement({
    background: '#4285F4',
    borderColor: '#1967D2',
    glyphColor: '#FFFFFF',
    glyph:
      routeData.value.waypoints.length > 0
        ? String.fromCharCode(66 + routeData.value.waypoints.length)
        : 'B',
    scale: 0.8,
  })

  routeMarkers.value.start = new AdvancedMarkerElement({
    map,
    position: routeData.value.origin,
    content: startPin.element,
  })

  const isStartAndEndSame =
    routeData.value.origin.lat === routeData.value.destination.lat &&
    routeData.value.origin.lng === routeData.value.destination.lng

  if (!isStartAndEndSame) {
    routeMarkers.value.end = new AdvancedMarkerElement({
      map,
      position: routeData.value.destination,
      content: endPin.element,
    })
  }
}

function renderRoute() {
  if (!googleMapsRef.value || !routeData.value) return

  const { directionsService, directionsRenderer } = googleMapsRef.value
  const { LatLng, TravelMode } = window.google.maps
  const { origin, destination, waypoints, departureTime } = routeData.value

  const isSameLocation =
    origin.lat === destination.lat && origin.lng === destination.lng

  directionsRenderer.setOptions({
    preserveViewport: isSameLocation,
  })

  directionsService
    .route({
      origin: new LatLng(origin.lat, origin.lng),
      destination: new LatLng(destination.lat, destination.lng),
      waypoints: waypoints.map((w) => ({
        location: new LatLng(w.lat, w.lng),
        stopover: true,
      })),
      travelMode: TravelMode.DRIVING,
      drivingOptions: {
        departureTime,
        trafficModel: 'pessimistic',
      },
    })
    .then((response: any) => {
      directionsRenderer.setDirections(response)
      createRouteMarkers()
      emit('onUpdateSegments', response.routes[0])
    })
    .catch((error: any) => {
      console.error('Route calculation failed:', error)
    })
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, _hoisted_3))
}
}

})